import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Scale, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";
import { negCard, RECON_STATUS_FR, RECON_TONE, RESOLUTION_FR, fmtTermValue } from "./negotiationShared";

const FILTERS = [
  ["unresolved", tfr("Non résolus")], ["all", tfr("Tous")], ["difference", tfr("Écarts")],
  ["missing", "Manquants"], ["new_unexpected", "Inattendus"], ["needs_review", tfr("À vérifier")], ["match", "Conformes"],
];

export function NegotiationReconciliation({ neg, documents, extractions, reconciliation, onChanged }) {
  const { tf, locale } = useLang();
  const finalDocs = documents.filter((d) => d.doc_kind === "final_contract");
  const [docId, setDocId] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("unresolved");
  const [idx, setIdx] = useState(0);
  const [confirmItem, setConfirmItem] = useState(null);
  const [note, setNote] = useState("");

  const rec = reconciliation;
  const selDoc = docId || rec?.document_id || finalDocs[finalDocs.length - 1]?.id || "";
  const hasExtraction = useMemo(() => extractions.some((x) => x.document_id === selDoc && x.status !== "not_found"), [extractions, selDoc]);

  if (!finalDocs.length && !rec) return null;

  const run = async () => {
    if (!selDoc) return;
    setBusy(true);
    try {
      await api.post(`/negotiations/${neg.id}/documents/${selDoc}/reconcile`);
      toast.success(tf("Réconciliation effectuée"));
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setBusy(false); }
  };

  const resolve = async (item, action, noteTxt) => {
    setBusy(true);
    try {
      const r = await api.post(`/negotiations/${neg.id}/reconciliation/${rec.id}/resolve`, { term_key: item.term_key, action, note: noteTxt || null });
      toast.success(r.data.overall === "reconciled" ? tf("Réconciliation complète — prêt pour la signature") : tf("Résolution enregistrée"));
      setConfirmItem(null); setNote("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setBusy(false); }
  };

  const items = rec?.items || [];
  const unresolved = (it) => it.status !== "match" && !it.resolution;
  const filtered = items.filter((it) => {
    if (filter === "unresolved") return unresolved(it);
    if (filter === "all") return true;
    return it.status === filter;
  });
  const counts = {
    match: items.filter((i) => i.status === "match").length,
    difference: items.filter((i) => i.status === "difference").length,
    missing: items.filter((i) => i.status === "missing").length,
    new_unexpected: items.filter((i) => i.status === "new_unexpected").length,
    needs_review: items.filter((i) => i.status === "needs_review").length,
  };
  const safeIdx = Math.min(idx, Math.max(0, filtered.length - 1));
  const fmt = (it, v) => fmtTermValue(it.vtype, it.currency, v, locale, tf);

  return (
    <div className={cn(negCard, "p-4")} data-testid="neg-reconciliation">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Scale className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85">{tf("Réconciliation du contrat final")}</h3>
        <div className="flex-1" />
        {rec && (
          <span className={cn("text-[11px] px-2.5 py-1 rounded-full border font-medium",
            rec.overall === "reconciled" ? "text-emerald-500 bg-emerald-500/10 border-emerald-500/25" : "text-amber-500 bg-amber-500/10 border-amber-500/25")}
            data-testid="recon-overall">
            {tf(rec.overall === "reconciled" ? "Réconcilié" : "Revue requise")}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {finalDocs.length > 0 && (
          <Select value={selDoc} onValueChange={setDocId}>
            <SelectTrigger className="h-9 w-auto min-w-[170px]" data-testid="recon-doc-select"><SelectValue /></SelectTrigger>
            <SelectContent>{finalDocs.map((d) => <SelectItem key={d.id} value={d.id}>{d.filename}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Button variant="outline" size="sm" className="h-9" disabled={busy || !selDoc} onClick={run} data-testid="recon-run-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
          {rec ? tf("Relancer la réconciliation") : tf("Lancer la réconciliation")}
        </Button>
        {!hasExtraction && selDoc && (
          <span className="text-[11px] text-muted-foreground">{tf("Analysez d'abord le contrat final pour extraire ses termes.")}</span>
        )}
      </div>

      {rec ? (
        <>
          <p className="text-xs text-muted-foreground mb-2" data-testid="recon-baseline">
            {rec.document_name} · {tf("comparé à la version convenue")} V{rec.baseline_version_number}
          </p>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="recon-counts">
            {Object.entries(counts).map(([k, c]) => (
              <span key={k} className={cn("text-[11px] px-2 py-0.5 rounded-full border", RECON_TONE[k])}>{c} {tf(RECON_STATUS_FR[k])}</span>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {FILTERS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => { setFilter(k); setIdx(0); }} data-testid={`recon-filter-${k}`}
                className={cn("text-[11px] px-2.5 py-1 rounded-full border transition-colors",
                  filter === k ? "border-red-500/50 text-red-500 bg-red-500/10" : "border-border dark:border-border text-muted-foreground hover:text-foreground")}>
                {tf(l)}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="recon-empty-filter">
              {filter === "unresolved" ? tf("Aucun point non résolu — la réconciliation est complète.") : tf("Aucun terme dans ce filtre.")}
            </p>
          ) : (
            <>
              <div className="hidden md:block space-y-2">
                {filtered.map((it) => (
                  <ReconItem key={it.term_key} it={it} rec={rec} fmt={fmt} busy={busy} resolve={resolve}
                    setConfirmItem={setConfirmItem} tf={tf} locale={locale} />
                ))}
              </div>
              <div className="md:hidden">
                <div className="flex items-center justify-between mb-2">
                  <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={safeIdx === 0} data-testid="recon-prev"><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="text-xs text-muted-foreground">{safeIdx + 1}/{filtered.length}</span>
                  <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => setIdx((i) => Math.min(filtered.length - 1, i + 1))} disabled={safeIdx >= filtered.length - 1} data-testid="recon-next"><ChevronRight className="h-4 w-4" /></Button>
                </div>
                <ReconItem it={filtered[safeIdx]} rec={rec} fmt={fmt} busy={busy} resolve={resolve}
                  setConfirmItem={setConfirmItem} tf={tf} locale={locale} mobile />
              </div>
            </>
          )}
          {rec.overall !== "reconciled" && (
            <p className="text-[11px] text-amber-500 mt-3" data-testid="recon-gate-warning">{tf("Vérifiez les différences contractuelles avant de continuer.")}</p>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="recon-empty">
          {tf("Vérifiez que le contrat final correspond à la dernière version convenue avant la signature.")}
        </p>
      )}

      <Dialog open={!!confirmItem} onOpenChange={(o) => { if (!o) { setConfirmItem(null); setNote(""); } }}>
        <DialogContent className="max-w-md" data-testid="recon-confirm-dialog">
          <DialogTitle>{tf("Accepter la modification finale ?")}</DialogTitle>
          <DialogDescription>
            {confirmItem && (<>
              {tf(confirmItem.label)} : {fmt(confirmItem, confirmItem.agreed)} → {fmt(confirmItem, confirmItem.final)}.{" "}
              {tf("Cette valeur du contrat final remplacera la valeur convenue. La version convenue reste conservée dans l'historique.")}
            </>)}
          </DialogDescription>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={tf("Note (facultatif)")} className="h-9" data-testid="recon-confirm-note" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" size="sm" onClick={() => { setConfirmItem(null); setNote(""); }}>{tf("Annuler")}</Button>
            <Button size="sm" disabled={busy} onClick={() => resolve(confirmItem, "accept_change", note)} data-testid="recon-confirm-accept">
              {tf("Accepter la modification")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReconItem({ it, rec, fmt, busy, resolve, setConfirmItem, tf, locale, mobile }) {
  return (
    <div className={cn("rounded-xl border border-border/60 dark:border-border p-3", it.status !== "match" && !it.resolution && "border-amber-500/40")}
      data-testid={`recon-item-${it.term_key}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-sm">{tf(it.label)}</span>
        <span className={cn("text-[10px] px-2 py-0.5 rounded-full border", RECON_TONE[it.status])} data-testid={`recon-status-${it.term_key}`}>{tf(RECON_STATUS_FR[it.status])}</span>
        <div className="flex-1" />
        {it.source_page && <span className="text-[10px] text-muted-foreground">{rec.document_name} · {tf("Page")} {it.source_page}</span>}
      </div>
      <div className={cn("mt-2 gap-2 text-sm", mobile ? "space-y-1.5" : "grid grid-cols-3")}>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{tf("Convenu")}</div>
          <div className="font-medium" data-testid={`recon-agreed-${it.term_key}`}>{it.agreed === null || it.agreed === undefined ? tf("Absent de la version convenue") : fmt(it, it.agreed)}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{tf("Contrat final")}</div>
          <div className="font-medium" data-testid={`recon-final-${it.term_key}`}>{it.final === null || it.final === undefined ? tf("Non détecté") : fmt(it, it.final)}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{tf("Différence")}</div>
          {it.delta !== null && it.delta !== undefined ? (
            <div className={cn("font-medium", it.delta > 0 ? "text-emerald-500" : "text-red-500")}>{it.delta > 0 ? "+" : ""}{fmt(it, it.delta)}</div>
          ) : it.status === "difference" ? (
            <div className="text-amber-500 text-xs font-medium">{fmt(it, it.agreed)} → {fmt(it, it.final)}</div>
          ) : <div className="text-muted-foreground">—</div>}
        </div>
      </div>
      {it.resolution ? (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className="text-[11px] text-emerald-500" data-testid={`recon-resolution-${it.term_key}`}>
            {tf(RESOLUTION_FR[it.resolution.action])} · {it.resolution.resolved_by} · {new Date(it.resolution.resolved_at).toLocaleDateString(locale)}
            {it.resolution.note ? ` — ${it.resolution.note}` : ""}
          </span>
          <button type="button" className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" disabled={busy}
            onClick={() => resolve(it, "reopen")} data-testid={`recon-reopen-${it.term_key}`}>
            <RotateCcw className="h-3 w-3" /> {tf("Rouvrir")}
          </button>
        </div>
      ) : it.status !== "match" && (
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          <Button variant="outline" size="sm" className="h-8" disabled={busy} onClick={() => resolve(it, "confirm_match")} data-testid={`recon-confirm-match-${it.term_key}`}>
            {tf("Confirmer conforme")}
          </Button>
          <Button variant="outline" size="sm" className="h-8" disabled={busy} onClick={() => setConfirmItem(it)} data-testid={`recon-accept-change-${it.term_key}`}>
            {tf("Accepter la modification")}
          </Button>
          <Button variant="outline" size="sm" className="h-8" disabled={busy} onClick={() => resolve(it, "request_correction")} data-testid={`recon-request-correction-${it.term_key}`}>
            {tf("Demander une correction")}
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-muted-foreground" disabled={busy} onClick={() => resolve(it, "needs_clarification")} data-testid={`recon-needs-clarification-${it.term_key}`}>
            {tf("À clarifier")}
          </Button>
        </div>
      )}
    </div>
  );
}
